import { useMemo, useState } from "react"
import { useQueryClient } from "@tanstack/react-query"
import { Button, Form, FormGroup, Label, Input, Spinner } from "reactstrap"
import ThemeModal from "components/Common/ThemeModal"
import FormikCreatableField from "components/Common/FormikCreatableField"
import PublishDraftSwitch from "components/Common/PublishDraftSwitch/PublishDraftSwitch"
import { useBulkUpdateStockStatusPublish } from "hooks/stockHook"
import {
  VEHICLE_IMPORTER_STOCKS_QUERY_KEY,
  VEHICLE_IMPORTER_QUERY_KEYS,
} from "hooks/useVehicleImporter"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import { useToastMessage } from "hooks/useToastMessage"
import { CanView } from "components/Common/CanView"
import { ACCESS } from "constants/accessPermissions"

const downloadMissingChassisList = missingChassisList => {
  if (!missingChassisList || missingChassisList.length === 0) return

  const now = new Date()
  const timestamp = now
    .toISOString()
    .replace(/[:.]/g, "-")
  const content = missingChassisList.join("\n")
  const blob = new Blob([content], { type: "text/plain;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")

  link.href = url
  link.download = `missing-chassis-${timestamp}.txt`
  document.body.appendChild(link)
  link.click()
  link.remove()
  URL.revokeObjectURL(url)
}

const TagImporterByFullChassis = ({ importerId, importer = {} }) => {
  const { handleResponse, handleError } = useToastMessage()
  const queryClient = useQueryClient()
  const [isOpen, setIsOpen] = useState(false)
  const toggle = () => setIsOpen(!isOpen)
  const [chassisList, setChassisList] = useState("")
  const [statusId, setStatusId] = useState("")
  const [readyStockTouched, setReadyStockTouched] = useState(false)
  const [isReadyStock, setIsReadyStock] = useState(false)

  const { isLoading: optionsLoading, raw } = useFilteredVehicleOptions({
    isAll: true,
  })

  const { mutateAsync: updateVehicles, isPending } =
    useBulkUpdateStockStatusPublish()

  const resolvedImporterId = importer && importer.id ? importer.id : importerId

  const statusFormik = useMemo(
    () => ({
      values: { vs_statuse_id: statusId },
      errors: {},
      touched: {},
      setTouched: () => {},
      setFieldValue: (name, value) => {
        if (name === "vs_statuse_id") {
          setStatusId(value)
        }
      },
    }),
    [statusId]
  )

  const resetForm = () => {
    setChassisList("")
    setStatusId("")
    setReadyStockTouched(false)
    setIsReadyStock(false)
  }

  const handleReadyStockToggle = value => {
    setReadyStockTouched(true)
    setIsReadyStock(value)
  }

  const refreshImporterStocks = () => {
    queryClient.invalidateQueries({
      predicate: query =>
        String(query.queryKey && query.queryKey[0]).indexOf(
          VEHICLE_IMPORTER_STOCKS_QUERY_KEY
        ) === 0,
    })
    queryClient.invalidateQueries({
      queryKey: [VEHICLE_IMPORTER_QUERY_KEYS.VEHICLE_IMPORTER],
    })
  }

  const handleSubmit = async e => {
    e.preventDefault()

    const full_chassis_list = chassisList
      .split(/\r?\n/)
      .map(s => s.trim())
      .filter(s => s.length > 0)

    if (full_chassis_list.length === 0) {
      return handleResponse(
        {
          success: false,
          message: "Please enter at least one chassis number.",
        },
        "",
        true
      )
    }

    if (!resolvedImporterId || Number.isNaN(Number(resolvedImporterId))) {
      return handleResponse(
        {
          success: false,
          message: "Importer is still loading. Please try again.",
        },
        "",
        true
      )
    }

    const payload = {
      importer_id: Number(resolvedImporterId),
      full_chassis_list,
    }

    if (statusId !== "") {
      payload.vs_statuse_id = statusId
    }

    if (readyStockTouched) {
      payload.vs_ready_stock = isReadyStock
    }

    try {
      const res = await updateVehicles(payload)
      const missingCount = res && res.data ? res.data.missing_chassis : 0
      const missingChassisList =
        res && res.data && res.data.missing_chassis_list
          ? res.data.missing_chassis_list
          : []
      const message = missingCount
        ? `${res.message || "Vehicle update completed."} ${missingCount} chassis not found.`
        : res.message || "Vehicle update completed."

      handleResponse(res, message, true)

      if (res.success && missingChassisList.length > 0) {
        downloadMissingChassisList(missingChassisList)
      }

      if (res.success) {
        resetForm()
        refreshImporterStocks()
        toggle()
      }
    } catch (error) {
      handleError(error)
    }
  }

  return (
    <CanView access={[ACCESS.STOCK.EDIT, ACCESS.STOCK.EDIT_IMPORTER]}>
      <Button
        size="sm"
        color="info"
        onClick={toggle}
        className="d-flex align-items-center gap-1 border-0"
        style={{ boxShadow: "none" }}
      >
        {/* handle with bx icon */}
        <i className="bx bx-tag font-size-16" />

        <span className="d-none d-sm-inline">Tag Vehicle</span>
      </Button>

      <ThemeModal
        isOpen={isOpen}
        toggle={toggle}
        title="Tag Vehicle by Full Chassis"
      >
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label for="chassisList">
              Chassis Numbers{" "}
              <small className="text-muted">(one per line)</small>
            </Label>

            <Input
              type="textarea"
              id="chassisList"
              rows="10"
              placeholder={`Example:
NKE165-7251102
MXPJ10-2023538
NRE161-0090201
ZWR80-0482845`}
              value={chassisList}
              onChange={e => setChassisList(e.target.value)}
            />
          </FormGroup>

          <FormikCreatableField
            name="vs_statuse_id"
            label="Status"
            formik={statusFormik}
            data={raw && raw.statuses ? raw.statuses : []}
            isLoading={optionsLoading}
            placeholder="Select Status"
          />

          <div className="card mb-3">
            <div className="card-header bg-light">
              <h6 className="mb-0">Ready Stock</h6>
            </div>
            <div className="card-body">
              <PublishDraftSwitch
                initialValue={isReadyStock}
                trueLabel="Ready Stock"
                falseLabel="Not Ready"
                trueIcon="bx-check-circle"
                falseIcon="bx-time-five"
                onToggle={handleReadyStockToggle}
              />
            </div>
          </div>

          <Button color="primary" type="submit" disabled={isPending}>
            {isPending ? <Spinner size="sm" /> : "Update Vehicles"}
          </Button>
        </Form>
      </ThemeModal>
    </CanView>
  )
}

export default TagImporterByFullChassis
